// D3 Stacked Normalized Horizontal Bar Chart for BodyOS data visualizations
// Shows proportions that sum to 100% (body composition, time allocation, etc.)
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  debounce
} from './utils'

export default class StackedNormalizedBarChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins, left: 120 },
      xLabel: '',
      yLabel: '',
      showLegend: true,
      showValues: true,
      animate: true,
      responsive: true,
      barHeight: 35,
      barPadding: 0.3,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)

    // Calculate height based on number of categories
    const numCategories = this.data.length
    const calculatedHeight = Math.max(
      this.options.height,
      this.options.margins.top + this.options.margins.bottom +
      (numCategories * (this.options.barHeight + 15))
    )
    const height = (width / this.options.width) * calculatedHeight

    // Calculate left margin based on longest label
    const maxLabelLength = Math.max(...this.data.map((d) => String(d.category).length))
    const estimatedLabelWidth = maxLabelLength * 7 + 20
    const margins = { ...this.options.margins }
    margins.left = Math.max(margins.left, estimatedLabelWidth)

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      margins
    )
    this.svg = svg

    this.tooltip = createTooltip(this.container)

    // Get all segment keys
    const keys = this.data[0].segments.map((s) => s.name)

    // Normalize data (sum to 100%)
    const normalizedData = this.data.map((d) => {
      const total = d3.sum(d.segments, (s) => s.value)
      const normalized = { category: d.category }
      d.segments.forEach((s) => {
        normalized[s.name] = (s.value / total) * 100
      })
      return normalized
    })

    // Create stack generator
    const stack = d3.stack()
      .keys(keys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone)

    const stackedData = stack(normalizedData)

    // Y scale (categories)
    const yScale = d3.scaleBand()
      .domain(this.data.map((d) => d.category))
      .range([0, innerHeight])
      .padding(this.options.barPadding)

    // X scale (0-100%)
    const xScale = d3.scaleLinear()
      .domain([0, 100])
      .range([0, innerWidth])

    // Draw axes
    this.drawAxes(g, xScale, yScale, innerWidth, innerHeight)

    // Color scale
    const colorScale = d3.scaleOrdinal()
      .domain(keys)
      .range(keys.map((k, i) => {
        const segment = this.data[0].segments.find((s) => s.name === k)
        return segment?.color || colors.chart[i % colors.chart.length]
      }))

    // Draw stacked bars
    const layers = g.selectAll('.layer')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('class', 'layer')
      .attr('fill', (d) => colorScale(d.key))

    const bars = layers.selectAll('.bar')
      .data((d) => d.map((segment) => ({ ...segment, key: d.key })))
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('y', (d) => yScale(d.data.category))
      .attr('height', yScale.bandwidth())
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      bars
        .attr('x', 0)
        .attr('width', 0)
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * 30)
        .ease(d3.easeCubicOut)
        .attr('x', (d) => xScale(d[0]))
        .attr('width', (d) => xScale(d[1]) - xScale(d[0]))
    } else {
      bars
        .attr('x', (d) => xScale(d[0]))
        .attr('width', (d) => xScale(d[1]) - xScale(d[0]))
    }

    // Add value labels if segments are wide enough
    if (this.options.showValues) {
      layers.selectAll('.bar-label')
        .data((d) => d.map((segment) => ({ ...segment, key: d.key })))
        .enter()
        .append('text')
        .attr('class', 'bar-label')
        .attr('x', (d) => xScale(d[0]) + (xScale(d[1]) - xScale(d[0])) / 2)
        .attr('y', (d) => yScale(d.data.category) + yScale.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('fill', (d) => this.getContrastColor(colorScale(d.key)))
        .attr('font-size', fonts.sizes.xs)
        .attr('font-family', fonts.family)
        .attr('font-weight', fonts.weights.medium)
        .attr('opacity', 0)
        .text((d) => {
          const barWidth = xScale(d[1]) - xScale(d[0])
          const value = d[1] - d[0]
          return barWidth > 35 ? `${Math.round(value)}%` : ''
        })
        .transition()
        .delay(this.options.animate ? animationConfig.duration : 0)
        .duration(300)
        .attr('opacity', 1)
    }

    // Add interactions
    this.addBarInteractions(bars, colorScale)

    // Legend
    if (this.options.showLegend) {
      const legendItems = keys.map((k, _i) => ({
        label: k,
        color: colorScale(k)
      }))
      createLegend(g, legendItems, {
        x: 0,
        y: -30,
        direction: 'horizontal'
      })
    }

    return this
  }

  drawAxes(g, xScale, yScale, innerWidth, innerHeight) {
    // Y axis (categories)
    const yAxis = g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale))

    yAxis.selectAll('text')
      .attr('fill', colors.text)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)
      .attr('font-weight', fonts.weights.medium)

    yAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    // X axis (percentage)
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(5).tickFormat((d) => `${d}%`))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    if (this.options.xLabel) {
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.xLabel.toUpperCase())
    }
  }

  addBarInteractions(bars, _colorScale) {
    const self = this

    bars
      .on('mouseover', function onMouseOver(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.8)

        const value = d[1] - d[0]
        const content = `
          <strong>${d.data.category}</strong><br/>
          <span style="color: ${colors.textMuted}">${d.key}: </span>
          <span style="color: ${colors.accent}">${value.toFixed(1)}%</span>
        `
        showTooltipHtml(self.tooltip, content, event)
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1)
        hideTooltip(self.tooltip)
      })
  }

  getContrastColor(bgColor) {
    const rgb = d3.color(bgColor)
    if (!rgb) return colors.text
    const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b
    return luminance > 150 ? colors.neutral[900] : colors.neutral.white
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
